import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const AppointmentScheduler = () => {
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [selectedNotary, setSelectedNotary] = useState('');
  const [serviceType, setServiceType] = useState('');
  const [clientInfo, setClientInfo] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const notaries = [
    { value: 'kouame-marie', label: 'Me. Marie Kouamé - Spécialiste Immobilier' },
    { value: 'traore-ibrahim', label: 'Me. Ibrahim Traoré - Droit des Affaires' },
    { value: 'diallo-fatou', label: 'Me. Fatou Diallo - Transactions Internationales' },
    { value: 'kone-bernard', label: 'Me. Bernard Koné - Propriété Foncière' }
  ];

  const serviceTypes = [
    { value: 'verification', label: 'Vérification de Propriété' },
    { value: 'transfer', label: 'Transfert de Titre' },
    { value: 'consultation', label: 'Consultation Juridique' },
    { value: 'documents', label: 'Préparation de Documents' },
    { value: 'inheritance', label: 'Succession Immobilière' }
  ];

  const availableTimes = [
    { value: '09:00', label: '09:00' },
    { value: '10:00', label: '10:00' },
    { value: '11:00', label: '11:00' },
    { value: '14:00', label: '14:00' },
    { value: '15:00', label: '15:00' },
    { value: '16:00', label: '16:00' }
  ];

  const handleInputChange = (field, value) => {
    setClientInfo((prev) => ({
      ...prev,
      [field]: value
    }));
    if (errors?.[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const clearError = (field) => {
    if (errors?.[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!serviceType) e.serviceType = 'Veuillez sélectionner un type de service';
    if (!selectedNotary) e.selectedNotary = 'Veuillez choisir un notaire';
    if (!selectedDate) {
      e.selectedDate = 'La date est requise';
    } else {
      const today = new Date();
      today?.setHours(0, 0, 0, 0);
      if (new Date(selectedDate) < today) e.selectedDate = 'La date doit être dans le futur';
    }
    if (!selectedTime) e.selectedTime = "L'heure est requise";
    if (!clientInfo?.name?.trim()) e.name = 'Le nom complet est requis';
    if (!clientInfo?.email?.trim()) {
      e.email = "L'adresse email est requise";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(clientInfo?.email)) {
      e.email = 'Adresse email invalide';
    }
    if (!clientInfo?.phone?.trim()) {
      e.phone = 'Le numéro de téléphone est requis';
    } else if (!/^[\d\s\+\-\(\)]{8,}$/?.test(clientInfo?.phone?.trim())) {
      e.phone = 'Numéro de téléphone invalide';
    }
    return e;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors)?.length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});
    setSubmitted(true);
    const notaryLabel = notaries?.find((n) => n?.value === selectedNotary)?.label;
    alert(`Rendez-vous programmé avec ${notaryLabel} le ${selectedDate} à ${selectedTime}`);
  };

  // Inline error helper
  const FieldError = ({ name }) =>
    errors?.[name] ? (
      <p className="mt-2 flex items-center gap-1.5 text-xs text-red-400 font-medium">
        <Icon name="AlertCircle" size={14} color="currentColor" />
        {errors?.[name]}
      </p>
    ) : null;

  return (
    <section className="py-24 bg-transparent border-none outline-none relative z-10">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl lg:text-5xl font-heading font-bold text-white mb-4 tracking-tight">
            Prendre Rendez-vous
          </h2>
          <p className="text-lg text-white/50 font-light max-w-2xl mx-auto">
            Planifiez votre consultation avec nos notaires partenaires, spécialisés dans la sécurisation des transactions immobilières.
          </p>
        </div>

        {/* Formulaire (Carte Premium #111111) */}
        <div className="bg-[#111111] rounded-3xl shadow-2xl border-none outline-none overflow-hidden">
          <div className="p-8 md:p-12">
            <form onSubmit={handleSubmit} className="space-y-8">
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* 
                  Note: Si le composant <Select> utilise bg-white en interne, 
                  vous devrez ajouter className="bg-white/5 border-none text-white" 
                  directement dans son composant enfant si ces props ne sont pas prises en compte.
                */}
                <div>
                  <Select
                    label={<span className="text-white/40 uppercase tracking-widest text-xs font-bold mb-2 block">Type de Service</span>}
                    placeholder="Sélectionnez un service"
                    options={serviceTypes}
                    value={serviceType}
                    onChange={(v) => { setServiceType(v); clearError('serviceType'); }}
                    error={errors?.serviceType}
                    required 
                  />
                  <FieldError name="serviceType" />
                </div>

                <div>
                  <Select
                    label={<span className="text-white/40 uppercase tracking-widest text-xs font-bold mb-2 block">Notaire Spécialisé</span>}
                    placeholder="Choisissez votre notaire"
                    options={notaries}
                    value={selectedNotary}
                    onChange={(v) => { setSelectedNotary(v); clearError('selectedNotary'); }}
                    error={errors?.selectedNotary}
                    required 
                  />
                  <FieldError name="selectedNotary" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <Input
                    label={<span className="text-white/40 uppercase tracking-widest text-xs font-bold mb-2 block">Date Souhaitée</span>}
                    type="date"
                    value={selectedDate}
                    onChange={(e) => { setSelectedDate(e?.target?.value); clearError('selectedDate'); }}
                    min={new Date()?.toISOString()?.split('T')?.[0]}
                    error={errors?.selectedDate}
                    required 
                  />
                  <FieldError name="selectedDate" />
                </div>

                <div>
                  <Select
                    label={<span className="text-white/40 uppercase tracking-widest text-xs font-bold mb-2 block">Heure Préférée</span>}
                    placeholder="Sélectionnez l'heure"
                    options={availableTimes}
                    value={selectedTime}
                    onChange={(v) => { setSelectedTime(v); clearError('selectedTime'); }}
                    error={errors?.selectedTime}
                    required 
                  />
                  <FieldError name="selectedTime" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <Input
                    label={<span className="text-white/40 uppercase tracking-widest text-xs font-bold mb-2 block">Nom Complet</span>}
                    type="text"
                    placeholder="Votre nom complet"
                    value={clientInfo?.name}
                    onChange={(e) => handleInputChange('name', e?.target?.value)}
                    error={errors?.name}
                    required 
                  />
                  <FieldError name="name" />
                </div>

                <div>
                  <Input
                    label={<span className="text-white/40 uppercase tracking-widest text-xs font-bold mb-2 block">Email</span>}
                    type="email"
                    placeholder="votre.email@exemple.com"
                    value={clientInfo?.email}
                    onChange={(e) => handleInputChange('email', e?.target?.value)}
                    error={errors?.email}
                    required 
                  />
                  <FieldError name="email" />
                </div>
              </div>

              <div>
                <Input
                  label={<span className="text-white/40 uppercase tracking-widest text-xs font-bold mb-2 block">Téléphone</span>}
                  type="tel"
                  placeholder="+225 XX XX XX XX XX"
                  value={clientInfo?.phone}
                  onChange={(e) => handleInputChange('phone', e?.target?.value)}
                  error={errors?.phone}
                  required 
                />
                <FieldError name="phone" />
              </div>

              <div>
                <label className="text-white/40 uppercase tracking-widest text-xs font-bold mb-2 block">
                  Message (Optionnel)
                </label>
                <textarea
                  className="w-full px-5 py-4 bg-white/5 border-none rounded-2xl text-white placeholder-white/20 focus:ring-2 focus:ring-brand-gold focus:outline-none resize-none transition-all shadow-inner"
                  rows="4"
                  placeholder="Décrivez brièvement votre situation ou vos questions..."
                  value={clientInfo?.message}
                  onChange={(e) => handleInputChange('message', e?.target?.value)} 
                />
              </div>

              {/* Global error summary */}
              {Object.keys(errors)?.length > 0 && (
                <div className="flex items-start gap-3 bg-red-500/10 border-none rounded-2xl p-5">
                  <Icon name="AlertCircle" size={20} className="text-red-400 flex-shrink-0 mt-0.5" />
                  <span className="text-sm font-medium text-red-400">
                    Veuillez corriger les {Object.keys(errors)?.length} erreur{Object.keys(errors)?.length > 1 ? 's' : ''} ci-dessus avant de confirmer le rendez-vous.
                  </span>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Button
                  type="submit"
                  variant="default"
                  iconName="Calendar"
                  iconPosition="left"
                  className="flex-1 bg-white text-black hover:bg-gray-200 rounded-full font-bold py-4 shadow-lg border-none hover:scale-105 transition-transform"
                >
                  Confirmer le Rendez-vous
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  iconName="Phone"
                  iconPosition="left"
                  className="bg-white/5 text-white hover:bg-white/10 rounded-full font-bold py-4 border-none transition-transform hover:scale-105"
                >
                  Appeler Directement
                </Button>
              </div>
            </form>
          </div>

          {/* Footer de la carte */}
          <div className="bg-white/[0.02] px-8 md:px-12 py-6 border-t border-white/5">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center shadow-inner">
                <Icon name="Check" size={20} className="text-emerald-400" />
              </div>
              <div>
                <p className="text-sm font-bold text-white mb-0.5">
                  Confirmation Immédiate
                </p>
                <p className="text-xs font-light text-white/50">
                  Vous recevrez une confirmation par email et SMS dans les minutes qui suivent.
                </p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default AppointmentScheduler;